<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import PageNavigation from '@components/admin/page-navigation'
import { fetchFundList } from '@apis/admin/finance'
import { fetchAssets } from '@apis/admin/asset'
import fromPairs from 'lodash/fromPairs'
import map from 'lodash/map'
import mapKeys from 'lodash/mapKeys'
import pick from 'lodash/pick'

export default {
  components: { PageNavigation },
  data() {
    return {
      loading: false,
      list: [],
      assets: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      accounts: [],
      excel_headers: [
        ['seq', this.$t('finance_fund_list.balance_unique_number')],
        ['member_seq', this.$t('finance_fund_list.member_unique_number')],
        ['user_id', this.$t('finance_fund_list.id')],
        ['user_name', this.$t('finance_fund_list.name')],
        ['email_address', this.$t('finance_fund_list.email_address')],
        ['amount', this.$t('finance_fund_list.balance')],
        ['total_amount', this.$t('finance_fund_list.accumulated_amount')],
        ['regdate', this.$t('finance_fund_list.first_payment_date')],
        ['asset_title', this.$t('finance_fund_list.asset_title')],
        ['asset_code', this.$t('finance_fund_list.asset_code')],
      ],
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList(), this.loadAssets()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadAssets() {
      const { data } = await fetchAssets()
      this.assets = data
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchFundList({
        ...this.$route.params,
        ...this.$route.query,
      })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search_params = pick(this.search_params, 'market_seq')
      this.search()
    },
    date,
    fromPairs,
    async handleExportExcel() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('finance_fund_list.msg_server_overload'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        const { data } = await fetchFundList({
          ...this.$route.params,
          ...this.$route.query,
          no_paging: 1,
        })

        const { default: exportJsonToExcel } = await import(
          '@utils/export-json-to-excel'
        )
        exportJsonToExcel({
          header: map(this.excel_headers, ([, title]) => title),
          data: this.format2excel(data),
          filename: this.$t('finance_fund_list.financial_details', [
            date('YmdHis'),
          ]),
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    format2excel(list) {
      const excel_fields = map(this.excel_headers, ([name]) => name)
      return map(list, (data) => {
        let result = pick(data, excel_fields)
        const keys = Object.keys(result)
        result = mapKeys(
          map(result, (val, key) => {
            switch (key) {
              case 'regdate':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              default:
                return val
            }
          }),
          (val, key) => {
            return keys[key]
          }
        )

        return map(excel_fields, (key) => result[key])
      })
    },
  },
}
</script>

<template>
  <div v-loading="loading">
    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['user_id', $t('admin.user_id')],
                      ['user_name', $t('admin.user_name')],
                      ['email_address', $t('admin.email_address')],
                      [
                        'fund_account_number',
                        $t('finance_fund_list.fund_account_number'),
                      ],
                      [
                        'execute_account_number',
                        $t('finance_fund_list.execution_account_number'),
                      ],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{
                  $t('finance_fund_list.statistics_exclude_members')
                }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.member_is_alive"
                :false-label="undefined"
                true-label="Y"
                >{{ $t('finance_fund_list.members_excluded') }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('asset') }}</div></th
            >
            <td>
              <select v-model="search_params.execute_asset_seq">
                <option
                  v-for="{ seq, title } in [
                    { seq: undefined, title: $t('admin.total') },
                    ...assets,
                  ]"
                  :key="seq"
                  :value="seq"
                  >{{ title }}</option
                >
              </select>
            </td>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [[undefined, $t('admin.regdate')]]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="clearAndSearch"
                ><span>{{ $t('admin.reset') }}</span></a
              >
              {{ ' ' }}
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{
      $t('finance_fund_list.financial details')
    }}</h3>
    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col" rowspan="2"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <template v-if="!$route.params.member_seq">
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_id') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_name') }}</div></th
            >
          </template>
          <th scope="col" :class="[$style.wide]"
            ><div>{{ $t('finance_fund_list.financial_product') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.asset_title') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('finance_fund_list.account_number') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('finance_fund_list.date_of_execution') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('finance_fund_list.execution_amount') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('finance_fund_list.cancellation_status') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('finance_fund_list.settlement_start') }}</div></th
          >
        </tr>
        <tr>
          <template v-if="!$route.params.member_seq">
            <th scope="col" colspan="2" :class="$style.quarter_wide"
              ><div>{{ $t('admin.email_address') }}</div></th
            >
          </template>
          <th scope="col"
            ><div>{{ $t('finance_fund_list.financial_instrument') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.modal_asset_code') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('finance_fund_list.execution') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('finance_fund_list.expiry_date') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('finance_fund_list.completed_or_not') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('finance_fund_list.revocation_type') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('finance_fund_list.registration_date') }}</div></th
          >
        </tr>
      </thead>
      <tbody>
        <template v-for="(data, idx) in list">
          <tr :key="`1-${data.seq}`" :class="$style[`bg${idx % 2}`]">
            <td rowspan="2" :class="[$style.tCenter, $style.nowrap]">{{
              data._no
            }}</td>
            <template v-if="!$route.params.member_seq">
              <td :class="[$style.tCenter, $style.nowrap]">{{
                data.user_id
              }}</td>
              <td :class="[$style.tCenter, $style.nowrap]">{{
                data.user_name
              }}</td>
            </template>
            <td :class="[$style.tCenter, $style.wide]">{{
              data.instrument_title
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.asset_title
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.fund_account_number
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              date('Y-m-d H:i:s', new Date(data.execute_start))
            }}</td>
            <td :class="[$style.tRight, $style.nowrap]"
              >{{ data.execute_amount | trimDecimalZero | numberFormat }}
              {{ data.asset_code }}</td
            >
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.is_closed
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              date('Y-m-d H:i:s', new Date(data.adjust_start))
            }}</td>
          </tr>
          <tr :key="`2-${data.seq}`" :class="$style[`bg${idx % 2}`]">
            <template v-if="!$route.params.member_seq">
              <td colspan="2" :class="[$style.tCenter, $style.nowrap]">{{
                data.email_address
              }}</td>
            </template>
            <td :class="[$style.tCenter, $style.wide]">{{
              fromPairs(constants.finance.fund.instrument_type)[
                data.instrument_type
              ]
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.asset_code
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.execute_account_number
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              date('Y-m-d H:i:s', new Date(data.execute_end))
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.is_fully_returned
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.close_type
                ? fromPairs(constants.finance.fund.close_type)[data.close_type]
                : '-'
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              date('Y-m-d H:i:s', new Date(data.regdate))
            }}</td>
          </tr>
        </template>
        <tr v-if="loading">
          <td
            :colspan="$route.params.member_seq ? 8 : 10"
            :class="$style.tCenter"
            >{{ $t('admin.loading') }}</td
          >
        </tr>
        <tr v-else-if="!list.length">
          <td
            :colspan="$route.params.member_seq ? 8 : 10"
            :class="$style.tCenter"
            >{{ $t('finance_fund_list.msg_no_financial') }}</td
          >
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}
</style>
